<template>
  <!-- Путь вместо ссылки «назад»: так поисковик видит структуру каталога,
       а покупателю доступны оба уровня, а не только предыдущий -->
  <Breadcrumbs :items="crumbs" />

  <h1 style="margin-top: 0; margin-bottom: 4px">{{ product_name }}</h1>
  <div style="margin-bottom: 10px; color: var(--el-text-color-regular)">
    Артикул: {{ formatVendorCode(vendor_code, selected_color) }}
  </div>

  <div style="max-width: 2200px; margin: 0 auto">
    <el-row>
      <el-col
        :xs="24"
        :sm="12"
        :md="11"
        :lg="9"
        :xl="6"
        style="display: flex; padding-bottom: 10px"
      >
        <!-- Миниатюры слева -->

        <!-- <div class="image-viewer-conatainer"> -->
        <el-scrollbar
          :style="{
            overflowY: 'auto',
            display: $device.isDesktop ? 'flex' : 'table',
            height: $device.isDesktop ? '400px' : 'revert-layer',
          }"
        >
          <div class="thumbnails">
            <img
              v-for="(image, index) in imagesByColor"
              :key="image.id"
              :src="getImageUrl(image.id, 200)"
              :alt="
                generateAltForImage(
                  'THUMB',
                  image,
                  `Миниатюра ${index + 1}. `,
                  ''
                )
              "
              :class="{ 'thumbnail-active': index === activeIndex }"
              @click="setActiveImage(index)"
              class="thumbnail"
              loading="lazy"
              decoding="async"
              :width="$device.isDesktop ? 80 : 60"
              :height="$device.isDesktop ? 80 : 60"
            />
          </div>
        </el-scrollbar>

        <!-- Основное изображение справа -->
        <div class="main-image-wrapper">
          <el-image
            ref="mainImage"
            class="main-photo"
            :src="getImageUrl(imagesByColor[activeIndex]?.id ?? null, 800)"
            :alt="
              generateAltForImage(
                'MAIN',
                imagesByColor[activeIndex],
                '',
                ` Фото №${activeIndex + 1}.`
              )
            "
            :preview-src-list="
              imagesByColor.map((image) => getImageUrl(image.id, 2000))
            "
            :initial-index="activeIndex"
            width="400"
            height="400"
          >
            <!-- Та же 200px, что в миниатюрах: она уже в кеше и видна сразу -->
            <template #placeholder>
              <img
                :src="getImageUrl(imagesByColor[activeIndex]?.id ?? null, 200)"
                alt=""
                aria-hidden="true"
                class="main-photo__low"
              />
            </template>
          </el-image>
        </div>
        <!-- </div> -->
      </el-col>
      <el-col :xs="24" :sm="12" :md="13" :lg="15" :xl="18">
          <!-- Контент справа от просмотра изображений. Рендерится на сервере:
               цена, характеристики и описание должны быть видны поисковику -->
          <div
            class="content-wrapper"
            :style="{
              marginLeft: isXs ? '0px' : '15px',
              paddingLeft: isXs ? '0px' : '15px',
            }"
          >
            <div
              v-if="disabled"
              style="font-size: 33px; color: var(--el-color-danger); font-weight: bold"
            >
              Товар временно не доступен
            </div>
            <!-- Цвет и выбор цветов -->
            <div
              ref="colorSelector"
              class="color-selector-wrapper"
              v-if="colorChoiceRequired"
            >
              <div class="color-selector-row">
                <div class="color-selector-label" v-if="$device.isDesktop">
                  Цвет:&nbsp;
                </div>
                <div class="color-selector-variants">
                  <el-radio-group
                    v-model="selected_color"
                    class="radio-group"
                    :style="{
                      justifyContent: $device.isDesktop ? 'start' : 'center',
                    }"
                  >
                    <el-radio-button
                      class="radio-button"
                      v-for="c in unique_colors"
                      :value="c.color_id"
                    >
                      <span
                        class="color-square"
                        :style="{ backgroundColor: c.bg_color }"
                      ></span>
                      <h4 class="color_cap">
                        {{ capitalizeFirstLetter(c.name) }}
                      </h4>
                    </el-radio-button>
                  </el-radio-group>
                </div>
              </div>
              <div v-if="colorHint" class="color-hint">
                Выберите цвет, чтобы добавить товар в корзину
              </div>
            </div>
            <!-- <div>
            Артикул: {{ vendor_code
            }}<span v-if="selected_color > 0">-{{ selected_color }}</span>
          </div> -->
            <div v-if="ozon_url || wb_url || ya_url" class="market-selector-label">
              <!-- <span>ya_url</span><br/> -->
              <span :style="{ fontSize: isXs ? '16px' : '22px' }"
                >Этот товар на:
              </span>
              <el-button
                v-if="ozon_url"
                :size="isXs ? 'small' : ''"
                type="primary"
                tag="a"
                :href="ozon_url"
                target="_blank"
                :onclick="trackOzonClick"
                rel="noopener noreferrer"
                color="#005bff"
                style="width: 75px; text-decoration: none"
                >OZON</el-button
              >
              <el-button
                v-if="wb_url"
                :size="isXs ? 'small' : ''"
                type="primary"
                tag="a"
                :href="wb_url"
                target="_blank"
                :onclick="trackWbClick"
                rel="noopener noreferrer"
                color="#c111b6"
                style="width: 75px; text-decoration: none"
                >WB</el-button
              >
              <el-button
                v-if="ya_url"
                :size="isXs ? 'small' : ''"
                type="primary"
                tag="a"
                :href="ya_url"
                target="_blank"
                :onclick="trackYaClick"
                rel="noopener noreferrer"
                color="#fd0"
                style="width: 100px; text-decoration: none"
                >Я.Маркет</el-button
              >
            </div>
            <div :style="{ fontSize: isXs ? '16px' : '22px' }">
              <!-- Цена видна всем, если это разрешено настройкой магазина -->
              <template v-if="pricesVisible && price !== null">
                Цена: {{ price }} ₽
                <span style="color: var(--el-border-color)">|</span>
              </template>
              <span style="white-space: nowrap"
                ><el-icon style="font-size: 17px"> <DCaret /> </el-icon>Размер:
                {{ formatSize(size_sm) }} см</span
              >
            </div>

            <!-- Наличие видно только тем, кому доступен магазин, и считается
                 по выбранному цвету: в белом может лежать, в чёрном — нет -->
            <div
              v-if="shopAccess && (orderable || availability.kind === 'none')"
              class="availability"
              :class="`availability--${availability.kind}`"
            >
              <el-icon>
                <IconFactory v-if="availability.kind !== 'stock'" :crossed="availability.kind === 'none'" />
                <Select v-else />
              </el-icon>
              {{ availability.text }}
            </div>

            <!-- Кнопка зависит от корзины в браузере, поэтому только на клиенте -->
            <ClientOnly>
              <div v-if="shopAccess" style="padding-top: 12px">
                <AddToCartButton
                  :product-id="product_id"
                  :color-id="cartColorId"
                  :disabled="!!disabled"
                  :orderable="orderable !== false"
                  :sold-out="availability.kind === 'none'"
                  :color-required="colorChoiceRequired"
                  size="large"
                  @color-required="onColorRequired"
                />
              </div>
            </ClientOnly>

            <div>
              <pre class="description"><b>Описание</b>: {{ description }}</pre>
            </div>
          </div>
      </el-col>
    </el-row>

    <!-- <div style="display: flex;">
    </div> -->

    <!-- Горизонтальный список карточек с похожими товарами -->
    <RelatedProducts :productId="product_id" :selectedColor="selected_color" />
    <br />
    <!-- <span style="font-size: 12px"
      ><sup>*</sup>Оптовая цена при покупке на сумму от 30 000р. При покупке на
      меньшую сумму цены будут выше и обговариваются в индивидуальном
      порядке.</span
    > -->
  </div>
</template>

<script setup>
import { watch, watchEffect, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { DCaret, Select } from "@element-plus/icons-vue";
import { formatSize, formatVendorCode } from "~/utils/formater";
import { jsonLdScript, productLd } from "~/utils/jsonLd";
import { useWindowSize } from "@vueuse/core";

const router = useRouter();
const route = useRoute();
const { $ym } = useNuxtApp();
const { width } = useWindowSize();
// До монтирования ширины окна нет, поэтому отталкиваемся от устройства:
// так разметка сервера и браузера совпадают и ничего не прыгает
const { isMobile } = useDevice();
const isXs = ref(isMobile);

const product_alias = route.params.product;
const category_alias = route.params.category;

const { product_id } = (await $fetch(
  `/api/product_id_by_alias?alias=${product_alias}`
)) ?? { product_id: null };

// Проверка на наличие product_id
if (!product_id) {
  throw createError({ statusCode: 404, message: "Упс =( тут товара нет!" });
}

const product = await $fetch(
  `/api/product_detail_by_id?product_id=${product_id}`
);
// Товар живёт в одном разделе, а находится только по своему алиасу — поэтому
// открывался и по старым адресам после переноса в другой раздел, и под любым
// разделом в адресе. Поисковики видели дубли (Вебмастер: одинаковые title),
// поэтому чужой раздел в адресе — постоянный редирект на настоящий
const homeCategory = product?.product?.category_alias;
const category_name = product?.product?.category_name;
if (homeCategory && homeCategory !== category_alias) {
  await navigateTo(
    { path: `/catalog/${homeCategory}/${product_alias}`, query: route.query },
    { redirectCode: 301, replace: true }
  );
}

const unique_colors = await $fetch(
  `/api/product_unique_colors?product_id=${product_id}`
);

const {
  product_name,
  price,
  size_sm,
  vendor_code,
  description,
  disabled,
  orderable,
  production_days,
  ozon_url,
  wb_url,
  ya_url,
} = product?.product;

const relatedProducts = ref([]);
const activeIndex = ref(0);
const selected_color = ref(null);
const imagesByColor = ref([]);

const shopAccess = useShopAccess();
const pricesVisible = usePricesVisible();

// У товара без вариаций единственный цвет служебный (99) — выбирать нечего.
// Во всех остальных случаях цвет должен указать покупатель
const colorChoiceRequired =
  !(unique_colors.length == 1 && unique_colors[0]?.color_id == 99);

// Раньше при пустом выборе подставлялся первый цвет товара: карточка
// открывается со всеми цветами сразу, и в корзину уезжал не тот, который
// покупатель смотрел. Теперь без выбора цвета кнопка попросит его указать
const cartColorId = computed(() => {
  if (selected_color.value !== null && selected_color.value >= 0) {
    return selected_color.value;
  }
  return colorChoiceRequired ? null : (unique_colors?.[0]?.color_id ?? null);
});

const colorSelector = ref(null);
const colorHint = ref(false);

const pluralDays = (count) => {
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) return "рабочих дней";
  const mod10 = count % 10;
  if (mod10 === 1) return "рабочий день";
  if (mod10 >= 2 && mod10 <= 4) return "рабочих дня";
  return "рабочих дней";
};

// Пока цвет не выбран, показываем сводку по товару: сколько всего лежит
// на складе. После выбора — остаток именно этого цвета
const availability = computed(() => {
  const days = Number(production_days) > 0 ? Number(production_days) : 0;
  const madeText = `Под заказ — изготовление ${days} ${pluralDays(days)}`;
  // Срок не задан — товар не производится: без склада купить его нельзя
  const made = days ? { kind: "made", text: madeText } : { kind: "none", text: "Нет в наличии — товар не производится" };

  const chosen =
    cartColorId.value === null
      ? null
      : unique_colors.find((c) => c.color_id === cartColorId.value);

  if (!chosen) {
    const total = unique_colors.reduce((sum, c) => sum + (Number(c.stock_qty) || 0), 0);
    if (total > 0) return { kind: "stock", text: `В наличии: ${total} шт. — выберите цвет` };
    return made;
  }

  const qty = Number(chosen.stock_qty) || 0;
  if (qty > 0) {
    const tail = days ? `, дальше под заказ (${days} ${pluralDays(days)})` : "";
    return { kind: "stock", text: `В наличии: ${qty} шт.${tail}` };
  }

  return made;
});

const onColorRequired = () => {
  colorHint.value = true;
  colorSelector.value?.scrollIntoView({ block: "center", behavior: "smooth" });
};

if (!route.query?.color) {
  imagesByColor.value = product.images;
}
//
const origin = useRequestURL().origin;
const productUrl = `${origin}/catalog/${category_alias}/${product_alias}`;

const crumbs = [
  { name: "Каталог", to: "/catalog" },
  { name: category_name, to: `/catalog/${category_alias}` },
  { name: product_name },
];

const productImages = (product?.images ?? [])
  .slice(0, 3)
  .map(
    (image) =>
      `${origin}/api/image/${category_alias}/${product_alias}/color/${image.color_alias}/${image.id}?size=800`
  );

// В сниппет уходит собственное описание товара, а не шаблон: человек в поиске
// читает то же, что увидит на странице
const metaDescription = (() => {
  const own = String(description ?? "").replace(/\s+/g, " ").trim();
  const size = size_sm ? `, высота ${formatSize(size_sm)} см` : "";
  const text = own
    ? `${product_name}${size}. ${own}`
    : `${product_name}${size} — изделие из художественного гипса, раздел «${category_name}». Производим с 1989 года, доставка по России.`;
  return text.length > 200 ? `${text.slice(0, 197).trim()}…` : text;
})();

useHead({
  title: `${product_name} — купить с доставкой по России`,
  meta: [
    { name: "description", content: metaDescription },
    { property: "og:title", content: product_name },
    { property: "og:description", content: metaDescription },
    { property: "og:type", content: "product" },
    { property: "og:url", content: productUrl },
    ...(productImages[0] ? [{ property: "og:image", content: productImages[0] }] : []),
  ],
  script: [
    jsonLdScript(
      productLd({
        origin,
        url: productUrl,
        name: product_name,
        description,
        images: productImages,
        vendorCode: vendor_code,
        categoryName: category_name,
        // Цену в разметке указываем только когда она видна на странице
        price: pricesVisible.value && price !== null ? price : null,
        available: !disabled && orderable !== false,
      })
    ),
  ],
  link: [
    {
      rel: "canonical",
      href: `${
        useRequestURL().origin
      }/catalog/${category_alias}/${product_alias}`,
    },
  ],
});

const trackOzonClick = () => {
  if ($ym) {
    $ym("reachGoal", "click_ozon");
  }
};

const trackWbClick = () => {
  if ($ym) {
    $ym("reachGoal", "click_wb");
  }
};

const trackYaClick = () => {
  if ($ym) {
    $ym("reachGoal", "click_ya");
  }
};

//ym(98738030,'reachGoal','click_wb')

const getImageAliasById = (id) => {
  const imgs = product?.images ?? [];
  return imgs.find((i) => i.id == id)?.color_alias ?? "FAIL";
};

// Функция для получения URL изображения по его ID
const getImageUrl = (id, size, p) => {
  if (!id) return `/img/no_img.png`;
  else if (id && !p)
    return `/api/image/${category_alias}/${product_alias}/color/${getImageAliasById(
      id
    )}/${id}${size ? "?size=" + size : ""}`;
  // Предположительно у вас есть API для получения изображения по ID
  else if (id && p)
    return `/api/image/${p.alias_category}/${p.name_alias}/color/primary/${id}${
      size ? "?size=" + size : ""
    }`; // Предположительно у вас есть API для получения изображения по ID
};

const setActiveImage = (index) => {
  activeIndex.value = index;
};

// Отслеживание выбора цвета
watch(
  () => selected_color.value,
  async (newId, oldId) => {
    if (newId >= 0) {
      // Цвет выбран — подсказка больше не нужна
      colorHint.value = false;
      const colorDetails = unique_colors.find((c) => c.color_id == newId);
      // Программное перенаправление на новую страницу с параметром color
      await router.push({
        path: `/catalog/${route.params.category}/${route.params.product}`,
        query: {
          color: colorDetails.alias, // Устанавливаем новый color в URL
        },
      });
    }
  }
);

// Функция для обработки изменения color
const handleColorIdChange = (newColorAlias) => {
  const colorDetails = unique_colors.find((c) => c.alias == newColorAlias);
  selected_color.value = parseInt(colorDetails?.color_id, null);
  if (product?.images && product.images.length) {
    activeIndex.value = 0;
    imagesByColor.value = product.images.filter(
      (i) => i?.color_id == colorDetails?.color_id
    );
  }
};

const generateAltForImage = (type, data, prefix = "", postfix = "") => {
  if (type == "MAIN") {
    return `Изделие "${product_name}", цвет ${data?.color_name}, категория "${category_name}".${postfix}`;
  }

  if (type == "THUMB") {
    return `${prefix}Изделие "${product_name}", цвет ${data?.color_name}, категория "${category_name}".`;
  }

  if (type == "RELATE") {
    return `${prefix}Изделие "${data.name}", категория "${data.name_category}".`;
  }
  return "TO DO";
};

// Отслеживание изменения параметра color_id в query
watch(
  () => route.query.color,
  (newColorAlias, oldColorAlias) => {
    if (newColorAlias !== oldColorAlias) {
      handleColorIdChange(newColorAlias);
    }
  }
);

// Обработка начального состояния color_id (если параметр уже есть при загрузке страницы)
if (route.query.color) {
  handleColorIdChange(route.query.color);
}

const capitalizeFirstLetter = (text) => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

onMounted(() => {
  // Счетчик просмотров
  $fetch(`/api/product_viewed?id=${product_id}`);
});

watchEffect(() => {
  // Изменяем значение, если ширина экрана меньше 768px
  isXs.value = width.value < 768 ? true : false;
});
</script>

<style scoped>
.color_cap {
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  margin: 0;
  display: inline-block;
}

.color-selector-wrapper {
  display: flex;
  flex-direction: column;
}

.color-selector-row {
  display: flex;
  align-items: center;
  margin-bottom: 0;
}

.availability {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-top: 8px;
  font-size: 15px;
}

.availability--stock {
  color: var(--el-color-success-dark-2);
}

/* Изготовление — тот же оранжевый, что у значка завода в админке */
.availability--made {
  color: var(--el-color-warning-dark-2);
}

/* Не производится и на складе нет */
.availability--none {
  color: var(--el-text-color-secondary);
}

/* Подсказка появляется, только если нажали «В корзину» без выбранного цвета */
.color-hint {
  padding: 2px 0 0 75px;
  font-size: 14px;
  color: var(--el-color-warning);
}

@media (max-width: 768px) {
  .color-hint {
    padding-left: 0;
    text-align: center;
  }
}

.color-selector-label {
  flex-shrink: 0;
  width: 75px;
  font-size: 22px;
  align-items: center;
}

.market-selector-label {
  flex-shrink: 0;
  font-size: 22px;
  align-items: center;
}

.color-selector-variants {
  flex-grow: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

/* Flex-контейнер для текста "Цвет:" и радиокнопок */
.color-selector {
  display: flex;
  align-items: center;
  /* Выравниваем текст и radio-button по вертикали */
  gap: 10px;
  /* Отступ между текстом и radio-buttons */
  flex-wrap: wrap;
  /* Позволяем радиокнопкам переноситься на новую строку */
}

.radio-group {
  display: flex;
  flex-wrap: wrap;
  /* Радиокнопки будут переноситься при нехватке места */
  gap: 5px;
  /* Отступ между радиокнопками */
  margin-bottom: 0 !important;
}

/* Радиокнопки */
.radio-button {
  margin-bottom: 0px;
  padding-left: 0px;
  display: flex;
  align-items: center;
  /* Выравнивание по центру по вертикали */
  border-left: 1px solid var(--el-border-color);
}

.radio-button .color-square {
  width: 13px;
  height: 9px;
  display: inline-block;
  border: 1px solid var(--el-border-color-light);
  margin-right: 3px;
  /* Отступ между квадратом и текстом */
}

/* Убираем левую границу у первой радиокнопки */
.radio-group .radio-button:first-child {
  border-left: none;
}

.back-link {
  font-size: 0.75em;
  color: var(--el-color-primary);
  text-decoration: none;
}

.back-link:hover {
  color: var(--sd-green-text);
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnails {
  display: flex;
  flex-direction: column;
  align-items: end;
  justify-content: space-between;
  height: 100%;
}

.thumbnail {
  /* width: 40px;
  height: 40px; */
  margin-bottom: 10px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border 0.2s;
}

.thumbnail-active {
  border: 2px solid #409eff;
}

.main-image-wrapper {
  display: flex;
  flex: 1;
  justify-content: start;
  align-items: start;
  max-width: 100%;
  /* Ограничить ширину просмотра изображений */
  /* height: 400px; */
  margin-left: 15px;
  /* width: 400px; */
}

/* Размер задан заранее: до загрузки фото блок схлопывается, и превью не видно */
.main-photo {
  width: 400px;
  max-width: 100%;
  aspect-ratio: 1 / 1;
}

.main-photo__low {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.main-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}

.content {
  flex-grow: 1;
  /* Контент будет занимать оставшееся пространство */
  padding-left: 15px;
  /* Отступ от изображения */
  display: flex;
  align-items: center;
  justify-content: center;
}

.content-wrapper {
  flex-grow: 1;
  padding-left: 15px;
  display: block;
  /* Устанавливаем block для переноса строк */
  margin-left: 15px;
}

.content-wrapper div {
  margin-bottom: 10px;
  /* Отступ между строками */
}

.description {
  font-family: "Montserrat";
  white-space: pre-wrap;
}

.image-viewer-conatainer {
  display: flex;
}
</style>
